
import { useCallback } from 'react';
import { TransportationDetails, Trip } from '@/types/itinerary';
import { toast } from 'sonner';
import { useTransportationFormState } from './useTransportationFormState';
import { useSaveTransportation } from './useSaveTransportation';

/**
 * Hook to manage transportation form data and saving functionality
 */
export const useTransportationForm = (
  trip: Trip | null,
  onSuccessfulSave: () => void
) => {
  const {
    outboundDetails,
    returnDetails,
    handleOutboundChange,
    handleReturnChange,
    resetForm,
    setOutboundDetails,
    setReturnDetails
  } = useTransportationFormState();
  
  const { saveTransportationToSupabase, isSaving } = useSaveTransportation(trip, onSuccessfulSave);

  // Save outbound journey details
  const saveOutboundDetails = useCallback(async () => {
    // Always save to local storage
    setOutboundDetails(outboundDetails);
    
    const savedToSupabase = await saveTransportationToSupabase(outboundDetails, 'Outbound');
    
    if (!savedToSupabase) {
      toast.success('Outbound travel details saved locally');
    }
    
    resetForm('outbound'); // Reset the form after saving
  }, [outboundDetails, setOutboundDetails, saveTransportationToSupabase, resetForm]);

  // Save return journey details
  const saveReturnDetails = useCallback(async () => {
    // Always save to local storage
    setReturnDetails(returnDetails);
    
    const savedToSupabase = await saveTransportationToSupabase(returnDetails, 'Return');
    
    if (!savedToSupabase) {
      toast.success('Return travel details saved locally');
    }
    
    resetForm('return'); // Reset the form after saving
  }, [returnDetails, setReturnDetails, saveTransportationToSupabase, resetForm]);

  return {
    outboundDetails,
    returnDetails,
    handleOutboundChange,
    handleReturnChange,
    saveOutboundDetails,
    saveReturnDetails,
    resetForm,
    isSaving
  };
};
